import { Clock } from 'lucide-react'
import type { TimelineItem } from '../lib/timeline'
import { DoseActionChips } from './DoseActionChips'
import { getDoseUrgency } from '../../../shared/lib/date-utils'

interface TimelineItemRowProps {
  item: TimelineItem
  onDoseTaken?: () => void
  onDoseMissed?: () => void
  isLoading?: boolean
  showActions?: boolean
}

/**
 * Renders a single timeline item (dose or appointment) in compact inline format
 */
export function TimelineItemRow({
  item,
  onDoseTaken,
  onDoseMissed,
  isLoading = false,
  showActions = true,
}: TimelineItemRowProps) {
  const formatTime = (time: Date) =>
    time.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })

  if (item.type === 'dose') {
    const dose = item.data
    const urgency = getDoseUrgency(dose.scheduledFor)

    return (
      <div className={`timeline-item timeline-item--dose timeline-item--${urgency}`}>
        <span className="timeline-item__icon">💊</span>
        <div className="timeline-item__content">
          <span className="timeline-item__name">
            {dose.medicationNombre} {dose.dosis}
          </span>
        </div>
        <span className="timeline-item__time">
          <Clock size={12} />
          {formatTime(item.time)}
        </span>
        {showActions && onDoseTaken && onDoseMissed && (
          <DoseActionChips
            onTaken={onDoseTaken}
            onMissed={onDoseMissed}
            isLoading={isLoading}
          />
        )}
      </div>
    )
  }

  if (item.type === 'appointment') {
    const appointment = item.data

    return (
      <div className="timeline-item timeline-item--appointment">
        <span className="timeline-item__icon">🩺</span>
        <div className="timeline-item__content">
          <span className="timeline-item__name">{appointment.motivo}</span>
          {appointment.veterinario && (
            <span className="timeline-item__subtitle">{appointment.veterinario}</span>
          )}
        </div>
        <span className="timeline-item__time">
          <Clock size={12} />
          {formatTime(item.time)}
        </span>
      </div>
    )
  }

  return null
}

interface TimelineItemDetailProps {
  item: TimelineItem
}

/**
 * Renders expanded details for a timeline item
 */
export function TimelineItemDetail({ item }: TimelineItemDetailProps) {
  if (item.type === 'dose') {
    const dose = item.data
    if (!dose.instrucciones) return null

    return (
      <div className="timeline-item-detail timeline-item-detail--dose">
        <span className="timeline-item-detail__label">💊 {dose.medicationNombre}:</span>
        <span className="timeline-item-detail__text">{dose.instrucciones}</span>
      </div>
    )
  }

  if (item.type === 'appointment') {
    const appointment = item.data
    const details: string[] = []

    if (appointment.veterinario) details.push(appointment.veterinario)
    if (appointment.clinica) details.push(appointment.clinica)

    if (details.length === 0) return null

    return (
      <div className="timeline-item-detail timeline-item-detail--appointment">
        <span className="timeline-item-detail__label">🩺 {appointment.motivo}:</span>
        <span className="timeline-item-detail__text">{details.join(', ')}</span>
      </div>
    )
  }

  return null
}
